import React, { useState } from 'react';

type FluxoModule = 'fiscal' | 'licitacoes';

interface FlowStep {
  title: string;
  description: string;
  detail?: string;
  file?: string;
}

interface StatusNode {
  name: string;
  color: string;
  transitions: string[];
}

const MODULE_CARDS: { id: FluxoModule; title: string; subtitle: string; icon: React.ReactNode }[] = [
  {
    id: 'fiscal',
    title: 'Fiscal (NFS-e / NFCom)',
    subtitle: 'Emissão de notas fiscais de serviço e comunicação',
    icon: <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>,
  },
  {
    id: 'licitacoes',
    title: 'Automação Licitações',
    subtitle: 'Propostas, disputa e monitoramento ComprasNet',
    icon: <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>,
  },
];

// ========== FISCAL FLOW DATA ==========

const FISCAL_PIPELINE: { title: string; icon: string; color: string }[] = [
  { title: 'Fatura', icon: '1', color: 'from-blue-500 to-blue-600' },
  { title: 'Split Fiscal', icon: '2', color: 'from-purple-500 to-purple-600' },
  { title: 'Transmissão', icon: '3', color: 'from-orange-500 to-orange-600' },
  { title: 'Autorização', icon: '4', color: 'from-green-500 to-green-600' },
  { title: 'Email/PDF', icon: '5', color: 'from-emerald-500 to-emerald-600' },
];

const FISCAL_STEPS: FlowStep[] = [
  {
    title: '1. Finalização da Fatura',
    description: 'Fatura finalizada emite evento invoice.finalized',
    detail: 'Campos-chave: code (FAT202604131), referenceMonth (mês de faturamento), periodStart/periodEnd (período do serviço), issueDate, items com fiscalType',
    file: 'invoices.service.ts → finalize()',
  },
  {
    title: '2. Classificação de Itens',
    description: 'Cada item é classificado pelo fiscalType do produto',
    detail: 'nfse → NFS-e (serviços TI/Cloud) | nfcom → NFCom (telefonia STFC/SIP) | isento → sem nota | CDR/Tarifas → sempre NFCom',
    file: 'fiscal-document.service.ts → classifyItems()',
  },
  {
    title: '3. Split Invoice',
    description: 'Cria documentos fiscais a partir da fatura',
    detail: 'Modo Individual: 1 NFS-e + 1 NFCom por fatura. Modo Consolidado: 1 NFS-e + 1 NFCom POR FILIAL (agrupa por locationId)',
    file: 'fiscal-document.service.ts → splitInvoice()',
  },
  {
    title: '4. Transmissão',
    description: 'Automático (fatura finalizada): batches de 3 com delay 2s. Lote manual: batches de 5 com ~4s entre lotes',
    detail: 'NFS-e → Prefeitura Cascavel (AtendeNet). NFCom → SEFAZ Federal. Reserva número sequencial com advisory lock (pg_advisory_xact_lock). "Transmitir em lote": batches de 5 a ~4s (~75 req/min, limite Focus 100/min). Retry de rate-limit automático: interceptor lê retry-after/"em N seg" e reenvia até 3×',
    file: 'nfse.service.ts / nfcom.service.ts → transmit()',
  },
  {
    title: '5. Polling de Autorização',
    description: '12 tentativas × 10s = 2 min. Se não autorizou, cron assume',
    detail: 'Crons: checkProcessingStatus (5min), autoTransmitPending (30min), retryFailed (4h, max 5x), retryMissingAttachments (15min)',
    file: 'fiscal-event.listener.ts → pollAuthorization()',
  },
  {
    title: '6. Download PDF/XML',
    description: 'Após autorização, baixa PDF e XML para S3',
    detail: 'S3: fiscal/nfse/{YYYY-MM}/{docId}_nfse.pdf e _nfse.xml | fiscal/nfcom/{YYYY-MM}/{docId}_nfcom.pdf e _nfcom.xml',
    file: 'nfse.service.ts / nfcom.service.ts → checkStatus()',
  },
  {
    title: '7. Envio por Email',
    description: 'Monta anexos e envia ao cliente',
    detail: 'Anexos: Fatura PDF + NFS-e PDF + NFCom PDF. Opcional: XMLs (includeXmlInEmail), ZIP (zipFiscalAttachments). BLOQUEIA se NF não autorizada',
    file: 'invoices.controller.ts → sendEmail()',
  },
];

const NFSE_VS_NFCOM: { aspecto: string; nfse: string; nfcom: string }[] = [
  { aspecto: 'O que é', nfse: 'Nota de Serviço', nfcom: 'Nota de Comunicação' },
  { aspecto: 'Regulador', nfse: 'Prefeitura (Cascavel)', nfcom: 'SEFAZ Federal' },
  { aspecto: 'Quando emite', nfse: 'Serviços TI/Cloud', nfcom: 'Telefonia (STFC/SIP)' },
  { aspecto: 'Imposto principal', nfse: 'ISS 4,20%', nfcom: 'ICMS (isento SN)' },
  { aspecto: 'Outros impostos', nfse: 'IBPT (informativo)', nfcom: 'PIS + COFINS + FUST + FUNTTEL' },
  { aspecto: 'Endereço cliente', nfse: 'Opcional', nfcom: 'OBRIGATÓRIO' },
  { aspecto: 'Contrato', nfse: 'Não obrigatório', nfcom: 'OBRIGATÓRIO' },
  { aspecto: 'Numeração', nfse: 'RPS (série municipal)', nfcom: 'Número sequencial' },
  { aspecto: 'Desconto', nfse: 'Campo vDesc separado', nfcom: 'Absorvido no vlr unitário' },
  { aspecto: 'Texto/Itens', nfse: 'Discriminação (texto plano)', nfcom: 'Itens estruturados (XML)' },
  { aspecto: 'Separador texto', nfse: '| (AtendeNet ignora \\n)', nfcom: 'N/A (itens XML)' },
  { aspecto: 'Competência', nfse: 'Ref: no texto (periodStart)', nfcom: 'data_faturamento (YYYYMM)' },
  { aspecto: 'Tipo assinante', nfse: 'N/A', nfcom: '1=comercial, 5=público' },
  { aspecto: 'Cancelamento', nfse: 'Via prefeitura', nfcom: 'Via SEFAZ (DELETE API)' },
];

const COMPETENCIA_RULES = {
  title: 'Lógica de Competência (Referência)',
  rule: 'Usar invoice.periodStart (não referenceMonth)',
  reason: 'Órgãos públicos faturam no mês seguinte ao serviço (emissão abril, serviço março)',
  examples: [
    { tipo: 'Órgão Público (FEPAM)', refMonth: '2026-04', periodStart: '2026-03-01', resultado: 'NFCom: 03/2026 | NFS-e Ref: Março/2026', hubFiltro: 'Abril' },
    { tipo: 'Cliente Privado', refMonth: '2026-04', periodStart: '2026-04-01', resultado: 'NFCom: 04/2026 | NFS-e Ref: Abril/2026', hubFiltro: 'Abril' },
  ],
  note: 'No banco: referenceMonth NÃO muda (organização Hub). Na nota: competência = mês real do serviço.',
};

const STATUS_FLOW: StatusNode[] = [
  { name: 'Rascunho', color: 'bg-slate-500', transitions: ['Processando'] },
  { name: 'Processando', color: 'bg-yellow-500', transitions: ['Autorizada', 'Erro', 'Rejeitada'] },
  { name: 'Autorizada', color: 'bg-green-500', transitions: ['Cancelada'] },
  { name: 'Erro', color: 'bg-red-500', transitions: ['Processando (retry)'] },
  { name: 'Rejeitada', color: 'bg-red-700', transitions: [] },
  { name: 'Cancelada', color: 'bg-slate-600', transitions: [] },
];

const CRONS = [
  { nome: 'checkProcessingStatus', intervalo: '5 min', acao: 'Polling docs em Processando (max 50, batch 3)' },
  { nome: 'autoTransmitPending', intervalo: '30 min', acao: 'Transmite Rascunhos pendentes (max 50, batch 5)' },
  { nome: 'retryFailed', intervalo: '4 horas', acao: 'Retenta Erros (max 5 retries, batch 5)' },
  { nome: 'retryMissingAttachments', intervalo: '15 min', acao: 'Re-download PDF/XML para Autorizadas sem anexo (max 15)' },
  { nome: 'checkCertificateExpiration', intervalo: 'diário 9h', acao: 'Alerta de expiração do certificado digital (A1)' },
];

const KEY_FILES = [
  { arquivo: 'fiscal-event.listener.ts', descricao: 'Escuta invoice.finalized → orquestra split + transmit + poll' },
  { arquivo: 'fiscal-document.service.ts', descricao: 'splitInvoice(), classifyItems(), syncInvoiceFiscalStatus()' },
  { arquivo: 'nfse/nfse.service.ts', descricao: 'transmit(), buildNfseJson(), buildDiscriminacao()' },
  { arquivo: 'nfcom/nfcom.service.ts', descricao: 'transmit(), buildNfcomJson()' },
  { arquivo: 'focus-nfe-api.service.ts', descricao: 'Wrapper HTTP Focus NFe (emitir, consultar, cancelar)' },
  { arquivo: 'fiscal.scheduler.ts', descricao: '5 crons automáticos (status 5min, auto-transmit 30min, retry 4h, anexos 15min, certificado diário 9h)' },
  { arquivo: 'invoices.controller.ts', descricao: 'sendEmail(), buildEmailAttachments()' },
];

// ========== COMPONENT ==========

export default function FluxosView({ aoAbrirPortal }: { aoAbrirPortal?: (portal: string) => void } = {}) {
  const [activeModule, setActiveModule] = useState<FluxoModule>('fiscal');
  const [expandedStep, setExpandedStep] = useState<number | null>(null);

  const renderFiscalFlow = () => (
    <div className="space-y-8">
      {/* Pipeline visual */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Pipeline de Emissão</h3>
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          {FISCAL_PIPELINE.map((step, i) => (
            <React.Fragment key={i}>
              <div className={`flex-shrink-0 flex items-center gap-3 px-5 py-3 rounded-central bg-gradient-to-r ${step.color} text-white shadow-lg`}>
                <span className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center font-bold text-sm">{step.icon}</span>
                <span className="font-medium text-sm whitespace-nowrap">{step.title}</span>
              </div>
              {i < FISCAL_PIPELINE.length - 1 && (
                <svg className="w-6 h-6 text-slate-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Steps detalhados */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Etapas Detalhadas</h3>
        <div className="space-y-2">
          {FISCAL_STEPS.map((step, i) => (
            <div key={i} className="border border-central-borda rounded-central overflow-hidden">
              <button
                onClick={() => setExpandedStep(expandedStep === i ? null : i)}
                className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-central-cartao transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="w-7 h-7 rounded-full bg-slate-600/30 text-slate-200 flex items-center justify-center text-xs font-bold">{i + 1}</span>
                  <div>
                    <span className="text-white font-medium text-sm">{step.title}</span>
                    <p className="text-slate-400 text-xs mt-0.5">{step.description}</p>
                  </div>
                </div>
                <svg className={`w-5 h-5 text-slate-400 transition-transform ${expandedStep === i ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {expandedStep === i && (
                <div className="px-5 pb-4 border-t border-central-borda">
                  <p className="text-slate-300 text-sm mt-3 leading-relaxed">{step.detail}</p>
                  {step.file && (
                    <p className="text-xs text-slate-500 mt-2 font-mono">
                      <span className="text-slate-500">arquivo:</span> {step.file}
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Competência */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">
          <span className="text-yellow-400 mr-2">★</span>
          {COMPETENCIA_RULES.title}
        </h3>
        <div className="bg-central-cartao border border-central-borda rounded-central p-5">
          <p className="text-white font-medium text-sm">{COMPETENCIA_RULES.rule}</p>
          <p className="text-slate-400 text-xs mt-1">{COMPETENCIA_RULES.reason}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
            {COMPETENCIA_RULES.examples.map((ex, i) => (
              <div key={i} className="bg-central-cartao rounded-lg p-3">
                <p className="text-white font-medium text-sm">{ex.tipo}</p>
                <div className="mt-2 space-y-1 text-xs">
                  <p className="text-slate-400">referenceMonth: <span className="text-slate-300">{ex.refMonth}</span></p>
                  <p className="text-slate-400">periodStart: <span className="text-slate-300">{ex.periodStart}</span></p>
                  <p className="text-white">{ex.resultado}</p>
                  <p className="text-slate-500">Hub filtro: {ex.hubFiltro}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-3 italic">{COMPETENCIA_RULES.note}</p>
        </div>
      </div>

      {/* Tabela comparativa */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">NFS-e vs NFCom</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-central-borda">
                <th className="text-left py-2 px-3 text-slate-400 font-medium">Aspecto</th>
                <th className="text-left py-2 px-3 text-slate-300 font-medium">NFS-e (Municipal)</th>
                <th className="text-left py-2 px-3 text-slate-300 font-medium">NFCom (Federal)</th>
              </tr>
            </thead>
            <tbody>
              {NFSE_VS_NFCOM.map((row, i) => (
                <tr key={i} className="border-b border-slate-800 hover:bg-central-cartao">
                  <td className="py-2 px-3 text-slate-300 font-medium">{row.aspecto}</td>
                  <td className="py-2 px-3 text-slate-400">{row.nfse}</td>
                  <td className="py-2 px-3 text-slate-400">{row.nfcom}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Ciclo de status */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Ciclo de Status</h3>
        <div className="flex flex-wrap gap-3">
          {STATUS_FLOW.map((status, i) => (
            <div key={i} className="bg-central-cartao border border-central-borda rounded-central p-4 min-w-[160px]">
              <div className="flex items-center gap-2 mb-2">
                <span className={`w-3 h-3 rounded-full ${status.color}`} />
                <span className="text-white font-medium text-sm">{status.name}</span>
              </div>
              {status.transitions.length > 0 ? (
                <div className="space-y-1">
                  {status.transitions.map((t, j) => (
                    <p key={j} className="text-xs text-slate-500">
                      → {t}
                    </p>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-600 italic">Estado final</p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Crons */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Crons Automáticos</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {CRONS.map((cron, i) => (
            <div key={i} className="bg-central-cartao border border-central-borda rounded-lg p-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-slate-200 font-mono text-xs">{cron.nome}</span>
                <span className="text-xs bg-central-elevado text-slate-300 px-2 py-0.5 rounded-full">{cron.intervalo}</span>
              </div>
              <p className="text-slate-400 text-xs">{cron.acao}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Arquivos-chave */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Arquivos-Chave</h3>
        <div className="space-y-1">
          {KEY_FILES.map((f, i) => (
            <div key={i} className="flex items-start gap-3 py-2 px-3 rounded-lg hover:bg-central-cartao">
              <span className="text-slate-300 font-mono text-xs whitespace-nowrap mt-0.5">{f.arquivo}</span>
              <span className="text-slate-400 text-xs">{f.descricao}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );


  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-white">Fluxos do Sistema</h2>
        <p className="text-sm text-slate-400 mt-1">Documentação visual dos processos automatizados</p>
      </div>

      {/* Module selector */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {MODULE_CARDS.map(mod => (
          <button
            key={mod.id}
            onClick={() => setActiveModule(mod.id)}
            className={`flex items-center gap-4 p-5 rounded-central border transition-all text-left ${
              activeModule === mod.id
                ? 'border-central-borda border-l-[3px] border-l-primary bg-central-elevado'
                : 'border-central-borda bg-central-cartao hover:bg-central-elevado'
            }`}
          >
            <div className={`${activeModule === mod.id ? 'text-white' : 'text-slate-500'}`}>
              {mod.icon}
            </div>
            <div>
              <p className={`font-semibold text-sm ${activeModule === mod.id ? 'text-white' : 'text-slate-200'}`}>{mod.title}</p>
              <p className="text-xs text-slate-400 mt-0.5">{mod.subtitle}</p>
            </div>
          </button>
        ))}
      </div>

      {/* Content */}
      {activeModule === 'fiscal' && renderFiscalFlow()}
      {/* Os fluxos de licitação saíram daqui (15/10/2026, aprovado pelo Heitor): eram um desenho fixo só do ComprasNet. Agora
          cada portal tem a sua página, gerada do registro do backend, na aba Portais. */}
      {activeModule === 'licitacoes' && (
        <div className="rounded-central border border-central-borda bg-central-cartao p-5 text-sm text-slate-300" data-fluxos-licitacoes-movidos>
          <p>Os fluxos de licitação agora ficam na aba <b>Portais</b>, com uma página por portal: etapas, variantes por modalidade e armadilhas, tiradas do registro do backend.</p>
          {aoAbrirPortal && (
            <button type="button" onClick={() => aoAbrirPortal('comprasnet')} className="mt-3 px-3 py-1.5 rounded-lg bg-central-cartao border border-central-borda hover:bg-central-elevado text-white text-xs">
              Abrir a página do ComprasNet
            </button>
          )}
        </div>
      )}
    </div>
  );
}
